import * as THREE from 'three';

const H = Math.sqrt(3) / 2;

/**
 * Trifuerza 3D dorada (R de Link): tres triángulos extruidos con un hueco entre
 * ellos, centrada en su origen y de cara a +Z. `size` es el ancho total.
 */
export function makeTriforce(size: number): THREE.Group {
  const group = new THREE.Group();
  const unit = size / 2;
  // vértices de los tres triángulos (lado 1) en un triángulo grande de lado 2
  const triangles: Array<Array<[number, number]>> = [
    [[-1, 0], [0, 0], [-0.5, H]],
    [[0, 0], [1, 0], [0.5, H]],
    [[-0.5, H], [0.5, H], [0, 2 * H]],
  ];
  const shapes = triangles.map((points) => {
    const cx = (points[0][0] + points[1][0] + points[2][0]) / 3;
    const cy = (points[0][1] + points[1][1] + points[2][1]) / 3;
    const shape = new THREE.Shape();
    points.forEach(([x, y], i) => {
      // se encoge un poco hacia su centro para dejar las ranuras de la Trifuerza
      const px = (cx + (x - cx) * 0.93) * unit;
      const py = (cy + (y - cy) * 0.93 - H) * unit;
      if (i === 0) shape.moveTo(px, py);
      else shape.lineTo(px, py);
    });
    shape.closePath();
    return shape;
  });
  const geometry = new THREE.ExtrudeGeometry(shapes, {
    depth: size * 0.05,
    bevelEnabled: true,
    bevelThickness: size * 0.015,
    bevelSize: size * 0.012,
    bevelSegments: 1,
  });
  geometry.translate(0, 0, -size * 0.025);
  const material = new THREE.MeshStandardMaterial({
    color: 0xffd04a,
    emissive: 0xffa31a,
    emissiveIntensity: 0.75,
    metalness: 0.85,
    roughness: 0.28,
    transparent: true,
    opacity: 1,
  });
  group.add(new THREE.Mesh(geometry, material));
  return group;
}

/**
 * Espada gigante estilo Espada Maestra que cae del cielo: hoja plateada, guarda
 * azul con alas, empuñadura morada y pomo dorado. El origen es la punta y la
 * hoja apunta hacia abajo (-Y). `length` es el largo total.
 */
export function makeGiantSword(length: number): THREE.Group {
  const group = new THREE.Group();
  const k = length / 5.6;
  const transparent = { transparent: true, opacity: 1 };
  // poco metálico: sin mapa de entorno, el metal puro se ve negro
  const steel = new THREE.MeshStandardMaterial({ color: 0xf4f8ff, metalness: 0.35, roughness: 0.3, emissive: 0x9cc8ff, emissiveIntensity: 0.45, ...transparent });
  const guardMat = new THREE.MeshStandardMaterial({ color: 0x4a66e0, metalness: 0.3, roughness: 0.4, emissive: 0x1a2a80, emissiveIntensity: 0.4, ...transparent });
  const gripMat = new THREE.MeshStandardMaterial({ color: 0x4a2a78, metalness: 0.2, roughness: 0.7, ...transparent });
  const goldMat = new THREE.MeshStandardMaterial({ color: 0xffcf4a, metalness: 0.9, roughness: 0.3, emissive: 0xa86a00, emissiveIntensity: 0.4, ...transparent });

  const tip = new THREE.Mesh(new THREE.ConeGeometry(0.26 * k, 0.7 * k, 4), steel);
  tip.rotation.x = Math.PI; // punta hacia abajo
  tip.rotation.y = Math.PI / 4;
  tip.scale.z = 0.3;
  tip.position.y = 0.35 * k;
  group.add(tip);

  const blade = new THREE.Mesh(new THREE.BoxGeometry(0.37 * k, 3.4 * k, 0.1 * k), steel);
  blade.position.y = (0.7 + 1.7) * k;
  group.add(blade);

  // guarda: barra central y dos alas inclinadas hacia la hoja
  const guardY = 4.1 * k + 0.12 * k;
  const guard = new THREE.Mesh(new THREE.BoxGeometry(0.75 * k, 0.26 * k, 0.24 * k), guardMat);
  guard.position.y = guardY;
  group.add(guard);
  for (const side of [-1, 1]) {
    const wing = new THREE.Mesh(new THREE.BoxGeometry(0.62 * k, 0.18 * k, 0.2 * k), guardMat);
    wing.position.set(side * 0.6 * k, guardY - 0.08 * k, 0);
    wing.rotation.z = side * -0.45;
    group.add(wing);
  }
  const gem = new THREE.Mesh(new THREE.OctahedronGeometry(0.11 * k), goldMat);
  gem.position.set(0, guardY, 0.13 * k);
  group.add(gem);

  const grip = new THREE.Mesh(new THREE.CylinderGeometry(0.09 * k, 0.1 * k, 1.0 * k, 8), gripMat);
  grip.position.y = guardY + 0.13 * k + 0.5 * k;
  group.add(grip);

  const pommel = new THREE.Mesh(new THREE.SphereGeometry(0.15 * k, 10, 8), goldMat);
  pommel.position.y = guardY + 0.13 * k + 1.08 * k;
  group.add(pommel);

  return group;
}

/** Libera geometrías y materiales de un grupo (efectos de un solo uso). */
export function disposeGroup(group: THREE.Object3D): void {
  group.traverse((object) => {
    const mesh = object as THREE.Mesh;
    if (!mesh.isMesh && !(object as THREE.Sprite).isSprite) return;
    if (mesh.isMesh) mesh.geometry.dispose();
    const material = mesh.material as THREE.Material | THREE.Material[];
    if (Array.isArray(material)) material.forEach((m) => m.dispose());
    else material.dispose();
  });
}

/** Opacidad común de todos los materiales del grupo (fundidos). */
export function setGroupOpacity(group: THREE.Object3D, opacity: number): void {
  group.traverse((object) => {
    const mesh = object as THREE.Mesh;
    if (!mesh.isMesh && !(object as THREE.Sprite).isSprite) return;
    const material = mesh.material as THREE.Material;
    material.opacity = opacity;
  });
}
